import { Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { concat, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { DataService } from 'src/app/services/data.service';
import { AlertService } from 'src/app/services/alert.service';
import { MasakComplianceService } from 'src/app/services/masak-compliance.service';
import { CustomerService } from 'src/app/services/customer.service';
import { Customer } from 'src/app/models/customer/customer';
import { CustomerType } from 'src/app/models/enums/customer-type';
import {
  CustomerIdentityVerificationAttachment,
  CustomerIdentityVerificationRequest,
  CustomerIdentityVerificationUpdateRequest,
  NewCustomerForVerification,
} from 'src/app/models/masak/masak-compliance';
import { CustomerIdentityDocumentType, CUSTOMER_IDENTITY_DOCUMENT_TYPE_OPTIONS } from 'src/app/models/enums/customer-identity-document-type';
import { TURKEY_PROVINCES } from 'src/app/models/masak/turkey-provinces.data';
import { SanctionScreeningResult, SanctionScreeningReviewStatus } from 'src/app/models/masak/masak-sanction-screening';
import { isSignatureEmpty, signatureError, SignatureValue } from 'src/app/core/helpers/signature.helpers';
import { isDocumentExpired, isTcknCardSerial, isValidTckn } from 'src/app/core/helpers/identity-document.helpers';
import Swal from 'sweetalert2';
import { IdentityOcrCancelledError, IdentityOcrService, IdentityOcrTimeoutError } from 'src/app/core/ocr/identity-ocr.service';
import { timeout } from 'rxjs/operators';
import { IdentityOcrResult, OcrFieldStatus } from 'src/app/core/ocr/identity-ocr.models';
import { isOcrSupportedFile } from 'src/app/core/ocr/image-preprocess';
import { identityNameMatchesCustomer } from 'src/app/core/ocr/name-match';
import { MasakIdentityCropModalComponent } from '../masak-identity-crop-modal/masak-identity-crop-modal.component';

/** OCR'ın doldurabildiği form alanları — "OCR" rozeti için. */
type OcrFormField = 'documentNumber' | 'tckn' | 'documentExpiryDate' | 'birthDate' | 'nationality' | 'firstName' | 'lastName';

const OCR_FIELD_LABELS: Record<OcrFormField, string> = {
  documentNumber: 'Seri No',
  tckn: 'T.C. Kimlik No',
  documentExpiryDate: 'Son Geçerlilik Tarihi',
  birthDate: 'Doğum Tarihi',
  nationality: 'Uyruğu',
  firstName: 'Ad',
  lastName: 'Soyad',
};

/** *ngFor'a her denetimde yeni dizi verilmesin (bkz. ocrRows). */
const NO_DISTRICTS: string[] = [];

/** MRZ uyruk kodu → formdaki uyruk metni. Listede olmayan ülkede ISO-3 kod yazılır (personel düzeltebilir). */
const NATIONALITY_NAMES: Record<string, string> = {
  TUR: 'Türkiye Cumhuriyeti', AZE: 'Azerbaycan', DEU: 'Almanya', RUS: 'Rusya', UKR: 'Ukrayna', GEO: 'Gürcistan',
  IRN: 'İran', IRQ: 'Irak', SYR: 'Suriye', AFG: 'Afganistan', TKM: 'Türkmenistan', UZB: 'Özbekistan',
  KAZ: 'Kazakistan', KGZ: 'Kırgızistan', BGR: 'Bulgaristan', GRC: 'Yunanistan', GBR: 'Birleşik Krallık',
  USA: 'Amerika Birleşik Devletleri', FRA: 'Fransa', NLD: 'Hollanda', SAU: 'Suudi Arabistan', ARE: 'Birleşik Arap Emirlikleri',
};
const nationalityText = (code?: string) => (code ? NATIONALITY_NAMES[code] ?? code : undefined);

const toTrDate = (iso?: string) => (iso ? iso.substring(0, 10).split('-').reverse().join('.') : '');

/** Eşiği aşan bir işlem için elle MASAK kimlik tespiti kaydı oluşturur — masak-uyum.md. */
@Component({
  selector: 'app-masak-compliance-create-modal',
  templateUrl: './masak-compliance-create-modal.component.html',
  styleUrls: ['./masak-compliance-create-modal.component.scss'],
})
export class MasakComplianceCreateModalComponent implements OnInit, OnChanges, OnDestroy {
  /** Bilgi amaçlı gösterilir; sunucu kaydı kendi güncel eşiğiyle damgalar. */
  @Input() thresholdAmountTl?: number;

  /** Verilirse düzenleme moduna geçer: müşteri bağlantısı sabit kalır, kayıt güncellenir. */
  @Input() editId?: number;

  /**
   * Exchange/Retail akışındaki MASAK eşik uyarısından açılırken müşteriyi önceden seçer
   * (ekrana dokunulmadan arama yapmaya gerek kalmasın diye) — "Kayıtlı Müşteri" seçiliyken
   * geçerli, editId ile birlikte kullanılmaz.
   */
  @Input() initialCustomerId?: number;
  @Input() initialCustomerName?: string;
  /** Aynı akıştan — sepete eklenen kalemlerin TL karşılığı, backend'in eşik kontrolünde kullandığı tutar. */
  @Input() initialTransactionAmountTl?: number;
  /**
   * Yalnızca Exchange akışındaki eşik uyarısından açılırken true — "Daha Sonra Gir" ile kimlik
   * tespiti doldurulmadan işleme devam edilebilir. MASAK Uyum ekranından elle açılan kayıtta
   * (editId ya da bu input olmadan) gösterilmez: orada atlanacak bir "işlem" yoktur.
   */
  @Input() allowSkip = false;

  /**
   * Nakit İşlem Beyan Formu'na gömülü kullanım: kimlik tespiti olmayan müşteride iki form tek
   * ekranda doldurulur. Başlık/alt bar, müşteri seçimi, işlem tutarı, mahiyet beyanı ve not
   * gizlenir — bunlar beyan formundan gelir. Kaydı beyan formu yapar (backend tek Commit ile iki
   * kaydı birlikte açar); bu bileşen yalnızca validate/getRequest/uploadSelectedDocuments sağlar.
   */
  @Input() embedded = false;

  /** Gömülü kullanımda beyan formunda seçili müşteri — değiştikçe kart yeniden okunur. */
  @Input() embeddedCustomerId?: number;

  loading = false;

  get isEdit(): boolean {
    return !!this.editId;
  }

  /** Tedbirler Yönetmeliği: bu bantta en az 20 karakterlik işlem mahiyeti beyanı zorunlu. */
  readonly purposeBandMinTl = 200000;
  readonly purposeBandMaxTl = 2000000;
  readonly purposeMinLength = 20;

  documentTypeOptions = CUSTOMER_IDENTITY_DOCUMENT_TYPE_OPTIONS;
  CustomerIdentityDocumentType = CustomerIdentityDocumentType;

  /** Yeni müşteri formundaki İl/İlçe kademeli seçimi — yalnızca bu form için (Customer.City/District genelde serbest metin kalır). */
  provinces = TURKEY_PROVINCES;

  get districtOptions(): string[] {
    return this.provinces.find((p) => p.il === this.newCustomer.city)?.ilceler ?? NO_DISTRICTS;
  }

  onProvinceChange() {
    this.newCustomer.district = undefined;
  }

  get companyDistrictOptions(): string[] {
    return this.provinces.find((p) => p.il === this.request.companyCity)?.ilceler ?? NO_DISTRICTS;
  }

  onCompanyProvinceChange() {
    this.request.companyDistrict = undefined;
  }

  CustomerType = CustomerType;

  /** Tüzel kişide şirket + temsile yetkili kişi bilgileri istenir; yetkilinin kimliği ortak belge alanlarında. */
  get isCompany(): boolean {
    return this.request.customerType === CustomerType.Company;
  }

  /**
   * Kayıtlı müşteride (ve düzenlemede) şirket bilgileri — unvan, VKN, telefon, il/ilçe, adres,
   * e-posta — formda SORULMAZ: backend kayda müşteri kartından kopyalar. Yalnızca yeni
   * müşteri açılırken girilir (kart bu bilgilerle açılır).
   */
  get usesCustomerCard(): boolean {
    return this.isEdit || this.embedded || !this.isNewCustomer;
  }

  /** Seçili müşterinin kartı — tüzel kişide şirket bilgilerini salt okunur göstermek için. */
  customerCard?: Customer;
  customerCardLoading = false;

  /** Backend ValidateCompanyCard ile aynı — kartta eksikse kayıt açılamaz. */
  get customerCardMissing(): string[] {
    const c = this.customerCard;
    if (!c) return [];
    const missing: string[] = [];
    if (!c.name?.trim()) missing.push('Şirket Unvanı');
    if (!c.taxNumber?.trim()) missing.push('Vergi Kimlik Numarası');
    if (!c.phone?.trim()) missing.push('Telefon Numarası');
    if (!c.shortAddress?.trim()) missing.push('Açık Adres');
    return missing;
  }

  setPersonType(type: CustomerType) {
    this.request.customerType = type;
    // Gerçek kişi kartından gelen TCKN yetkilinin TCKN'si değildir.
    if (type === CustomerType.Company && this.prefilledTckn && this.request.tckn === this.prefilledTckn) {
      this.request.tckn = undefined;
      this.prefilledTckn = undefined;
    }
    // Kimlikten okunan ad yeni türün alanlarına (yeni müşteri ↔ yetkili) taşınsın.
    if (this.ocrResult) this.applyOcrFields(this.ocrResult, ['firstName', 'lastName']);
  }

  /** Ek belge başına azami boyut — backend MasakComplianceController.AttachmentMaxBytes ile aynı. */
  readonly attachmentMaxBytes = 10 * 1024 * 1024;
  readonly attachmentAccept = 'application/pdf,image/jpeg,image/png';
  /** Kayıttan sonra yüklenecek yeni ek belgeler. */
  pendingAttachments: File[] = [];
  /** Düzenlemede kayda zaten bağlı ek belgeler — silme anında sunucuya gider. */
  existingAttachments: CustomerIdentityVerificationAttachment[] = [];
  deletingAttachmentId?: number;

  /** T.C. Kimlik Kartı'nda ön/arka yüz AYRI yüklenir; diğer belge türlerinde tek dosya (foto ya da PDF) yeterli. */
  get isTckn(): boolean {
    return this.request.documentType === CustomerIdentityDocumentType.Tckn;
  }

  get isPassport(): boolean {
    return this.request.documentType === CustomerIdentityDocumentType.Passport;
  }

  /** Kimlikten okuma (OCR): T.C. Kimlik Kartı (iki yüz, TD1) ve pasaport (fotoğraflı sayfa, TD3). */
  get ocrEnabled(): boolean {
    return this.isTckn || this.isPassport;
  }

  /** TCKN alanı: kimlik kartında zorunlu; pasaportta isteğe bağlı (Türk pasaportunda MRZ'de var). */
  get showTcknField(): boolean {
    return this.isTckn || this.isPassport;
  }

  /** T.C. Kimlik Kartı'nda belge numarası kartın seri no'su; TCKN ayrı alanda (masak-kimlik-ocr.md). */
  get documentNumberLabel(): string {
    if (this.isTckn) return this.isCompany ? 'Yetkilinin Kimlik Kartı Seri No *' : 'Seri No *';
    if (this.isPassport) return this.isCompany ? 'Yetkilinin Pasaport No *' : 'Pasaport No *';
    return this.isCompany ? 'Kimlik Belgesi Numarası *' : 'Belge Numarası *';
  }

  /**
   * Düzenlenen kayıt seri no alanından (SUB_057) önce açılmışsa belge no TCKN'nin kendisiydi —
   * alan boş gösterilir; seri no girilmezse kayıt eski değeriyle (TCKN) korunur.
   */
  private legacyDocumentNumber?: string;

  get legacySerialMissing(): boolean {
    return !!this.legacyDocumentNumber;
  }

  /** Kayıtlı müşterinin kartından ön doldurulan TCKN — müşteri değişince yenisiyle değişsin diye. */
  private prefilledTckn?: string;

  get tcknInvalid(): boolean {
    return this.showTcknField && !!this.request.tckn && !isValidTckn(this.request.tckn.trim());
  }

  get serialFormatUnexpected(): boolean {
    const serial = this.request.documentNumber?.trim();
    return this.isTckn && !!serial && !isTcknCardSerial(serial);
  }

  get documentExpired(): boolean {
    return isDocumentExpired(this.request.documentExpiryDate as any);
  }

  /** Gerçek kişide seçili müşterinin kartında başka bir TCKN var — kaydederken kartın güncellenmesi sorulur. */
  get tcknDiffersFromCard(): boolean {
    const card = this.customerCard;
    const tckn = this.request.tckn?.trim();
    return this.showTcknField && !this.isCompany && this.usesCustomerCard && !!card && card.customerType !== CustomerType.Company
      && !!card.tckn?.trim() && !!tckn && isValidTckn(tckn) && card.tckn.trim() !== tckn;
  }

  private _documentFrontFile?: File;
  private _documentBackFile?: File;

  get documentFrontFile(): File | undefined {
    return this._documentFrontFile;
  }

  set documentFrontFile(file: File | undefined) {
    this._documentFrontFile = file;
    this.refreshIdentityUploadItems();
  }

  get documentBackFile(): File | undefined {
    return this._documentBackFile;
  }

  set documentBackFile(file: File | undefined) {
    this._documentBackFile = file;
    this.refreshIdentityUploadItems();
  }
  /** Düzenlemede mevcut belge(ler) varsa — görüntüleme linki için. */
  existingDocumentUrl?: string;
  existingDocumentBackUrl?: string;

  /** false = kayıtlı müşteri seçilir, true = yeni müşteri kartı da bu kayıtla açılır. */
  isNewCustomer = false;
  newCustomer: NewCustomerForVerification = {};

  request: CustomerIdentityVerificationRequest = {
    customerType: CustomerType.Person,
    documentType: CustomerIdentityDocumentType.Tckn,
    nationality: 'Türkiye Cumhuriyeti',
  };
  saving = false;

  // Ekranda atılan imza — kayıt kaydedildikten sonra kendi ucuna gönderilir (belgelerle aynı).
  // Gömülü kullanımda (nakit beyan formu) gösterilmez: beyanın kendi imza alanı var.
  signature: SignatureValue | null = null;
  existingSignatureSvg?: string | null;
  existingSignatureRemoved = false;

  customer$: Observable<any>;
  customersLoading = false;
  customerInput$ = new Subject<string>();

  constructor(
    public activeModal: NgbActiveModal,
    private dataService: DataService,
    private alertService: AlertService,
    private service: MasakComplianceService,
    private customerService: CustomerService,
    private router: Router,
    private ocrService: IdentityOcrService,
    private modalService: NgbModal,
    private zone: NgZone,
  ) {}

  ngOnDestroy(): void {
    this.ocrService.cancel();
  }

  // ── Kimlikten okuma (OCR) — masak-kimlik-ocr.md §5 ───────────────────────────
  //
  // Görseller tarayıcıda okunur (IdentityOcrService); sonuç panelde gösterilir, "Forma Aktar" ile
  // alanlara yazılır — dolu ve farklı alanlar onaysız değişmez. Kırpılan görsel yalnızca OCR'a gider,
  // yüklenen belge özgün dosya kalır.

  ocrReading = false;
  ocrStep = '';
  ocrProgress = 0;
  private _ocrResult?: IdentityOcrResult;

  get ocrResult(): IdentityOcrResult | undefined {
    return this._ocrResult;
  }

  set ocrResult(value: IdentityOcrResult | undefined) {
    this._ocrResult = value;
    this.refreshOcrRows();
  }
  ocrError?: string;
  private ocrToken = 0;
  /** Forma aktarılan değerler — alan hâlâ bu değeri taşıyorsa "OCR" rozeti gösterilir. */
  private ocrApplied: Partial<Record<OcrFormField, string>> = {};
  /** "Kartı Kırp" sonrası OCR'a verilecek kırpılmış görseller. */
  private ocrFrontOverride?: File;
  private ocrBackOverride?: File;

  /**
   * Kimlik kartında okuma iki yüzün fotoğrafı da seçildiğinde (arka yüz MRZ doğrular, ön yüz adı
   * verir); pasaportta fotoğraflı sayfa seçilince (MRZ ve ad aynı sayfada).
   */
  get canReadOcr(): boolean {
    if (this.isPassport) return isOcrSupportedFile(this.documentFrontFile);
    return this.isTckn && isOcrSupportedFile(this.documentFrontFile) && isOcrSupportedFile(this.documentBackFile);
  }

  /** Okuma için eksik kalan yüz — ipucu metni için. */
  get ocrMissingSide(): 'front' | 'back' | null {
    if (!this.isTckn) return null;
    const front = isOcrSupportedFile(this.documentFrontFile);
    const back = isOcrSupportedFile(this.documentBackFile);
    if (front && !back) return 'back';
    if (back && !front) return 'front';
    return null;
  }

  get hasUnsupportedDocumentFile(): boolean {
    return [this.documentFrontFile, this.documentBackFile].some((f) => !!f && !isOcrSupportedFile(f));
  }

  /** Kırpılacak yüz: MRZ okunamadıysa arka, ad okunamadıysa ön yüz. */
  get ocrCropSide(): 'front' | 'back' | null {
    const backOk = isOcrSupportedFile(this.documentBackFile);
    const frontOk = isOcrSupportedFile(this.documentFrontFile);
    if (this.isPassport) return frontOk ? 'front' : null;
    if (backOk && !this.ocrResult?.mrzFound) return 'back';
    if (frontOk && this.ocrResult?.status.name !== 'verified') return 'front';
    return backOk ? 'back' : frontOk ? 'front' : null;
  }

  /**
   * Sonuç tablosu satırları — ocrResult atanınca BİR KEZ hesaplanır. *ngFor'a her değişiklik
   * denetiminde yeni dizi/nesne veren getter, BrowserAnimationsModule ile sonsuz döngü kuruyordu
   * (satırlar her denetimde yeniden eklenir → animasyon motoru mikro görev planlar → yeni denetim)
   * ve sayfa kilitleniyordu. Bu bileşende *ngFor'a getter bağlamayın.
   */
  ocrRows: { label: string; value?: string; status: OcrFieldStatus }[] = [];

  private refreshOcrRows() {
    const r = this.ocrResult;
    if (!r) {
      this.ocrRows = [];
      return;
    }
    const name = [r.firstName, r.lastName].filter(Boolean).join(' ');
    this.ocrRows = [
      { label: 'T.C. Kimlik No', value: r.tckn, status: r.status.tckn },
      { label: r.documentKind === 'passport' ? 'Pasaport No' : 'Seri No', value: r.serialNumber, status: r.status.serialNumber },
      { label: 'Ad Soyad', value: name, status: r.status.name },
      { label: 'Doğum Tarihi', value: toTrDate(r.birthDate), status: r.status.birthDate },
      { label: 'Son Geçerlilik', value: toTrDate(r.expiryDate), status: r.status.expiryDate },
    ];
  }

  trackOcrRow = (_: number, row: { label: string }) => row.label;

  get ocrHasValues(): boolean {
    const r = this.ocrResult;
    return !!r && !!(r.tckn || r.serialNumber || r.birthDate || r.expiryDate || r.firstName);
  }

  get ocrExpired(): boolean {
    return isDocumentExpired(this.ocrResult?.expiryDate);
  }

  /** Kayıtlı gerçek kişi müşteride kimlikten okunan ad carinin adıyla uyuşmuyor (K7, §5.6). */
  get ocrNameMismatch(): boolean {
    const r = this.ocrResult;
    if (!r?.firstName || !r.lastName || !this.ocrEnabled || this.isCompany || !this.usesCustomerCard || !this.customerCard) return false;
    return !identityNameMatchesCustomer(r.firstName, r.lastName, this.customerCard.name);
  }

  /**
   * Uyuşmazlık kaydı ENGELLER — ama yalnızca ad iki yüzden doğrulandıysa. Adın bir kısmı yalnızca
   * MRZ'den geldiyse (okuma hatası olabilir) uyarıdır: OCR hatası kaydı bloklamamalı.
   */
  get ocrNameMismatchBlocks(): boolean {
    return this.ocrNameMismatch && this.ocrResult?.status.name === 'verified';
  }

  ocrStatusText(status: OcrFieldStatus): string {
    return status === 'verified' ? '✔ Doğrulandı' : status === 'unverified' ? '⚠ Kontrol edin' : '— Okunamadı';
  }

  ocrStatusIcon(status: OcrFieldStatus): string {
    return status === 'verified' ? 'mdi-check-circle' : status === 'unverified' ? 'mdi-alert-circle' : 'mdi-minus-circle-outline';
  }

  ocrFilled(field: OcrFormField): boolean {
    const applied = this.ocrApplied[field];
    return applied !== undefined && this.ocrFieldValue(field) === applied;
  }

  async readIdentity() {
    if (!this.canReadOcr) return;
    const token = ++this.ocrToken;
    this.ocrReading = true;
    this.ocrError = undefined;
    this.ocrResult = undefined;
    this.ocrStep = 'OCR bileşenleri yükleniyor…';
    this.ocrProgress = 0;
    try {
      // Angular bölgesi DIŞINDA: Tesseract worker'ı yüzlerce ilerleme mesajı gönderiyor; bölge içinde
      // her mesaj tüm sayfada değişiklik denetimi tetikliyor. Ekran yalnızca adım değişince ya da
      // ilerleme %5 artınca güncellenir.
      let shownStep = '';
      let shownProgress = 0;
      const front = this.ocrFrontOverride ?? this.documentFrontFile;
      const back = this.ocrBackOverride ?? this.documentBackFile;
      const isPassport = this.isPassport;
      const result = await this.zone.runOutsideAngular(() => {
        const onProgress = (p: { step: string; progress: number }) => {
          if (token !== this.ocrToken) return;
          if (p.step === shownStep && p.progress - shownProgress < 0.05 && p.progress < 1) return;
          shownStep = p.step;
          shownProgress = p.progress;
          this.zone.run(() => {
            this.ocrStep = p.step;
            this.ocrProgress = p.progress;
          });
        };
        return isPassport ? this.ocrService.readPassport(front, onProgress) : this.ocrService.read(front, back, onProgress);
      });
      if (token !== this.ocrToken) return;
      this.zone.run(() => {
        this.ocrResult = result;
        if (!this.ocrHasValues) {
          this.ocrError = 'Kart okunamadı. "Kartı Kırp" ile kartın sınırlarını seçip yeniden deneyin ya da bilgileri elle girin.';
          return;
        }
        this.applyOcrFields(result);
        // Müşteri araması okumayı (ve kaydetmeyi) bekletmez; sonucu gelince kip/seçim güncellenir.
        this.resolveCustomerByTckn(result, token);
      });
    } catch (e) {
      if (e instanceof IdentityOcrCancelledError) return;
      this.zone.run(() => {
        if (token !== this.ocrToken) return;
        this.ocrError = e instanceof IdentityOcrTimeoutError
          ? 'Kimlik okuması zaman aşımına uğradı. "Yeniden Oku" ile tekrar deneyin ya da bilgileri elle girin.'
          : 'Kimlik okunamadı (OCR bileşenleri yüklenemedi ya da görsel açılamadı). Bilgileri elle girebilirsiniz.';
      });
    } finally {
      this.zone.run(() => {
        if (token === this.ocrToken) this.ocrReading = false;
      });
    }
  }

  async cropAndRead() {
    const side = this.ocrCropSide;
    const file = side === 'back' ? this.documentBackFile : side === 'front' ? this.documentFrontFile : undefined;
    if (!side || !file) return;
    const ref = this.modalService.open(MasakIdentityCropModalComponent, { size: 'lg', backdrop: 'static' });
    ref.componentInstance.file = file;
    ref.componentInstance.sideText = this.isPassport ? 'Fotoğraflı Sayfa' : side === 'back' ? 'Arka Yüz' : 'Ön Yüz';
    let cropped: File;
    try {
      cropped = await ref.result;
    } catch {
      return; // vazgeçildi
    }
    if (side === 'back') this.ocrBackOverride = cropped;
    else this.ocrFrontOverride = cropped;
    await this.readIdentity();
  }

  /** Kayıtlı müşteri araması sonucu — panelde gösterilir. */
  ocrCustomerNotice?: string;

  /** Personelin elle girdiği ve kimlikten farklı okunan değerler: korunur, "Kimliktekini kullan" ile değiştirilebilir. */
  ocrKeptManual: { field: OcrFormField; label: string; current: string; value: string }[] = [];

  /**
   * Okunan değerleri forma KENDİLİĞİNDEN yazar: boş alanlar ve önceki okumanın doldurduğu alanlar
   * güncellenir; personelin elle girdiği farklı değer korunur (ocrKeptManual). Ad kimlikteki gibi
   * (K4); kayıtlı gerçek kişide ad müşteri kartından gelir — yazılmaz, karşılaştırılır.
   */
  private applyOcrFields(r: IdentityOcrResult, only?: OcrFormField[]) {
    const proposals: { field: OcrFormField; value: string }[] = [];
    const add = (field: OcrFormField, value: string | undefined, status: OcrFieldStatus) => {
      if (value && status !== 'missing' && (!only || only.includes(field))) proposals.push({ field, value });
    };
    add('documentNumber', r.serialNumber, r.status.serialNumber);
    add('tckn', r.tckn, r.status.tckn);
    add('documentExpiryDate', r.expiryDate, r.status.expiryDate);
    add('birthDate', r.birthDate, r.status.birthDate);
    const nationality = nationalityText(r.nationalityCode);
    if (nationality) add('nationality', nationality, 'verified');
    if (this.isCompany || !this.usesCustomerCard) {
      add('firstName', r.firstName, r.status.name);
      add('lastName', r.lastName, r.status.name);
    }

    const kept = this.ocrKeptManual.filter((k) => only && !only.includes(k.field));
    const isDate = (f: OcrFormField) => f === 'birthDate' || f === 'documentExpiryDate';
    for (const p of proposals) {
      const current = this.ocrFieldValue(p.field);
      if (!current || current === p.value || current === this.ocrApplied[p.field]) {
        this.setOcrField(p.field, p.value);
        this.ocrApplied[p.field] = p.value;
      } else {
        kept.push({
          field: p.field,
          label: p.field === 'documentNumber' && r.documentKind === 'passport' ? 'Pasaport No' : OCR_FIELD_LABELS[p.field],
          current: isDate(p.field) ? toTrDate(current) : current,
          value: isDate(p.field) ? toTrDate(p.value) : p.value,
        });
      }
    }
    this.ocrKeptManual = kept;
    if (!only) this.request.documentType = r.documentKind === 'passport' ? CustomerIdentityDocumentType.Passport : CustomerIdentityDocumentType.Tckn;
    // Kimlikten gelen TCKN, karttan ön doldurulan değer değil — müşteri değişince silinmesin.
    if (this.ocrApplied.tckn && this.request.tckn === this.ocrApplied.tckn) this.prefilledTckn = undefined;
  }

  /** Elle girilmiş değerin yerine kimlikten okunanı yazar. */
  useOcrValue(field: OcrFormField) {
    const r = this.ocrResult;
    if (!r) return;
    const value = {
      documentNumber: r.serialNumber,
      tckn: r.tckn,
      documentExpiryDate: r.expiryDate,
      birthDate: r.birthDate,
      nationality: nationalityText(r.nationalityCode),
      firstName: r.firstName,
      lastName: r.lastName,
    }[field];
    if (!value) return;
    this.setOcrField(field, value);
    this.ocrApplied[field] = value;
    this.ocrKeptManual = this.ocrKeptManual.filter((k) => k.field !== field);
  }

  /**
   * Yeni kayıtta müşteri seçilmemişse kimlikteki TCKN ile kayıtlı müşteri aranır: tek kart varsa
   * "Kayıtlı Müşteri" ile o seçilir, yoksa "Yeni Müşteri" seçilir (kart okunan bilgilerle açılır),
   * birden fazlaysa liste o kartlarla açılır ve personel seçer. Personel müşteriyi zaten seçtiyse
   * seçime dokunulmaz — ad uyuşmazlığı denetimi devreye girer.
   */
  private async resolveCustomerByTckn(r: IdentityOcrResult, token: number) {
    this.ocrCustomerNotice = undefined;
    if (this.isEdit || this.embedded || this.isCompany || !r.tckn || r.status.tckn === 'missing') return;
    if (this.request.customerId) return;

    let matches: { value: any; text: string }[];
    this.ocrCustomerSearching = true;
    try {
      const res = await this.dataService.getCustomer({ tckn: r.tckn, isActive: true }).pipe(timeout(8000)).toPromise();
      matches = (res?.data ?? []) as any[];
    } catch {
      return;
    } finally {
      if (token === this.ocrToken) this.ocrCustomerSearching = false;
    }
    if (token !== this.ocrToken || this.request.customerId) return;
    // TCKN tekildir; çok sayıda sonuç, filtreyi bilmeyen (güncellenmemiş) API'nin bütün müşterileri döndürmesidir.
    if (matches.length > 5) return;

    if (matches.length === 1) {
      if (this.isNewCustomer) this.setCustomerMode(false);
      this.customer$ = this.buildCustomerStream(matches);
      this.request.customerId = matches[0].value;
      this.onCustomerSelected(matches[0].value);
      this.ocrCustomerNotice = `Bu TCKN ile kayıtlı müşteri seçildi: ${matches[0].text}.`;
    } else if (matches.length > 1) {
      if (this.isNewCustomer) this.setCustomerMode(false);
      this.customer$ = this.buildCustomerStream(matches);
      this.ocrCustomerNotice = `Bu TCKN ile ${matches.length} müşteri kartı var — listeden seçiniz.`;
    } else {
      if (!this.isNewCustomer) this.setCustomerMode(true);
      this.ocrCustomerNotice = 'Bu TCKN ile kayıtlı müşteri yok — yeni müşteri kartı açılacak.';
    }
    // Kip değişimi yeni müşteri alanlarını sıfırlar — kimlikten okunan ad yeniden yazılsın.
    this.applyOcrFields(r, ['firstName', 'lastName']);
  }

  /** Kimlikteki TCKN ile kayıtlı müşteri aranıyor (en fazla 8 sn). */
  ocrCustomerSearching = false;

  /** Süren okumayı keser — form elle doldurulabilir. */
  cancelOcr() {
    this.clearOcr();
  }

  /** Kimlik dosyası seçilince gerçek kişiye geçilir — tüzel kişi kayıtlı müşteride ve düzenlemede değil. */
  private ensurePersonForIdentityFile() {
    if (this.isEdit || !this.isCompany) return;
    if (this.usesCustomerCard && this.customerCard?.customerType === CustomerType.Company) return;
    this.setPersonType(CustomerType.Person);
  }

  /**
   * "Eklenecek Belgeler" listesinde gösterilen kimlik dosyaları — kayıttan sonra ön/arka yüz olarak
   * yüklenir. Dosya ya da belge türü değişince BİR KEZ hesaplanır (getter → sonsuz döngü, bkz. ocrRows).
   */
  identityUploadItems: { side: 'front' | 'back'; label: string; file: File }[] = [];

  trackIdentityUpload = (_: number, item: { side: string }) => item.side;

  private refreshIdentityUploadItems() {
    const items: { side: 'front' | 'back'; label: string; file: File }[] = [];
    if (this.documentFrontFile) {
      items.push({ side: 'front', label: this.isTckn ? 'Kimlik ön yüz' : this.isPassport ? 'Pasaport' : 'Kimlik belgesi', file: this.documentFrontFile });
    }
    if (this.isTckn && this.documentBackFile) {
      items.push({ side: 'back', label: 'Kimlik arka yüz', file: this.documentBackFile });
    }
    this.identityUploadItems = items;
  }

  @ViewChild('frontFileInput') private frontFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('backFileInput') private backFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('singleFileInput') private singleFileInput?: ElementRef<HTMLInputElement>;

  removeIdentityFile(side: 'front' | 'back') {
    if (side === 'front') {
      this.documentFrontFile = undefined;
      this.ocrFrontOverride = undefined;
      if (this.frontFileInput) this.frontFileInput.nativeElement.value = '';
      if (this.singleFileInput) this.singleFileInput.nativeElement.value = '';
    } else {
      this.documentBackFile = undefined;
      this.ocrBackOverride = undefined;
      if (this.backFileInput) this.backFileInput.nativeElement.value = '';
    }
    this.onIdentityImagesChanged();
  }

  private ocrFieldValue(field: OcrFormField): string {
    const r = this.request;
    switch (field) {
      case 'documentNumber': return r.documentNumber?.trim() ?? '';
      case 'tckn': return r.tckn?.trim() ?? '';
      case 'documentExpiryDate': return r.documentExpiryDate ? String(r.documentExpiryDate).substring(0, 10) : '';
      case 'birthDate': return r.birthDate ? String(r.birthDate).substring(0, 10) : '';
      case 'nationality': return r.nationality?.trim() ?? '';
      case 'firstName': return (this.isCompany ? r.representativeFirstName : this.newCustomer.firstName)?.trim() ?? '';
      case 'lastName': return (this.isCompany ? r.representativeLastName : this.newCustomer.lastName)?.trim() ?? '';
    }
  }

  private setOcrField(field: OcrFormField, value: string) {
    const r = this.request;
    switch (field) {
      case 'documentNumber': r.documentNumber = value; break;
      case 'tckn': r.tckn = value; break;
      case 'documentExpiryDate': r.documentExpiryDate = value as any; break;
      case 'birthDate': r.birthDate = value as any; break;
      case 'nationality': r.nationality = value; break;
      case 'firstName':
        if (this.isCompany) r.representativeFirstName = value;
        else this.newCustomer.firstName = value;
        break;
      case 'lastName':
        if (this.isCompany) r.representativeLastName = value;
        else this.newCustomer.lastName = value;
        break;
    }
  }

  private clearOcr() {
    this.ocrService.cancel();
    this.ocrToken++;
    this.ocrReading = false;
    this.ocrResult = undefined;
    this.ocrError = undefined;
    this.ocrKeptManual = [];
    this.ocrCustomerNotice = undefined;
    this.ocrCustomerSearching = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.embedded && changes.embeddedCustomerId) {
      this.request.customerId = this.embeddedCustomerId;
      this.onCustomerSelected(this.embeddedCustomerId);
    }
  }

  async ngOnInit(): Promise<void> {
    // Düzenleme: mevcut kaydı çek, ng-select'in ilk emisyonuna da müşteriyi koy
    // ki typeahead'e dokunulmadan seçili müşterinin adı görünsün.
    let initialCustomerItems: { value: number; text: string }[] = [];

    if (this.editId) {
      this.loading = true;
      try {
        const res = await this.service.find(this.editId).toPromise();
        if (res.isSuccess && res.data) {
          this.request = { ...res.data, customerType: res.data.customerType ?? CustomerType.Person };
          // <input type="date"> yalnızca yyyy-MM-dd gösterir; sunucu ISO tarih-saat döner.
          if (this.request.birthDate) this.request.birthDate = String(this.request.birthDate).substring(0, 10) as any;
          if (this.request.documentExpiryDate) {
            this.request.documentExpiryDate = String(this.request.documentExpiryDate).substring(0, 10) as any;
          }
          if (this.isTckn && this.request.tckn && this.request.documentNumber?.trim() === this.request.tckn) {
            this.legacyDocumentNumber = this.request.documentNumber.trim();
            this.request.documentNumber = '';
          }
          this.existingAttachments = res.data.attachments ?? [];
          this.existingDocumentUrl = res.data.documentImageUrl;
          this.existingDocumentBackUrl = res.data.documentImageBackUrl;
          this.existingSignatureSvg = res.data.customerSignatureSvg;
          if (res.data.customerId && res.data.customerName) {
            initialCustomerItems = [{ value: res.data.customerId, text: res.data.customerName }];
          }
          // Güncellemede şirket bilgileri kartın güncel hâlinden yeniden kopyalanır — onu göster.
          this.loadCustomerCard(res.data.customerId);
        }
      } catch {
        this.alertService.showErrorsToast(['Kayıt okunamadı.']);
      } finally {
        this.loading = false;
      }
    } else if (this.initialCustomerId) {
      this.request.customerId = this.initialCustomerId;
      let name = this.initialCustomerName;
      if (!name) {
        try {
          const res = await this.dataService.getCustomer({ id: this.initialCustomerId, isActive: true }).toPromise();
          name = res?.data?.[0]?.text ?? '';
        } catch {
          name = '';
        }
      }
      initialCustomerItems = [{ value: this.initialCustomerId, text: name }];
      this.onCustomerSelected(this.initialCustomerId);
    }

    if (!this.editId && this.initialTransactionAmountTl) {
      this.request.transactionAmountTl = this.initialTransactionAmountTl;
    }

    this.customer$ = this.buildCustomerStream(initialCustomerItems);
  }

  /**
   * Müşteri seçim listesi: önce verilen kalemler (düzenlemede kayıtlı müşteri, kimlikten okunan
   * TCKN'nin kartı…) — ng-select seçili müşterinin adını aramaya dokunulmadan göstersin — sonra arama.
   */
  private buildCustomerStream(initialItems: { value: any; text: string }[]): Observable<any> {
    return concat(
      of(initialItems),
      this.customerInput$.pipe(
        filter((term) => term !== null),
        distinctUntilChanged(),
        debounceTime(200),
        tap(() => (this.customersLoading = true)),
        switchMap((term) =>
          this.dataService
            .getCustomer({ name: term, isActive: true })
            .toPromise()
            .then((res) => {
              this.customersLoading = false;
              return res.data;
            }),
        ),
      ),
    );
  }

  get purposeRequired(): boolean {
    const amount = this.request.transactionAmountTl ?? 0;
    return amount >= this.purposeBandMinTl && amount <= this.purposeBandMaxTl;
  }

  /**
   * "Daha Sonra Gir" — kimlik tespiti kaydı girilmeden işleme devam eder (ForceToAction:true ile
   * yeniden denenir). Kayıt hâlâ girilmemiş olacağından işlem, alım/satım listesinde "Kimlik
   * Tespiti Eksik" rozetiyle işaretli kalır; takip için oradan geriye dönülebilir.
   */
  skip() {
    this.alertService
      .showConfirmAlert(
        'Kimlik tespiti kaydı girilmeden işleme devam edilecek. Bu işlem, kaydı girilene kadar alım/satım listesinde "Kimlik Tespiti Eksik" olarak görünecektir.',
        'Devam Et',
        'Geri Dön',
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.activeModal.close('skip');
        }
      });
  }

  /** Kayıtlı müşteri seçilince kartı okunur; kart tüzel kişiyse form tüzel kişiye geçer. */
  onCustomerSelected(customerId?: number) {
    this.loadCustomerCard(customerId, true);
  }

  /**
   * Eksik bilgili müşteri kartını düzeltmek için müşteri detayını YENİ SEKMEDE açar —
   * modal Alım/Satım ve Hızlı Satış akışında da açılıyor; aynı sekmede gidilirse sepet
   * ve yarım kalan form kaybolur. Kart düzeltilince "Kartı Yenile" ile uyarı kalkar.
   */
  openCustomerCard() {
    if (!this.request.customerId) return;
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/specifications/customer/detail', this.request.customerId]),
    );
    window.open(url, '_blank');
  }

  refreshCustomerCard() {
    this.loadCustomerCard(this.request.customerId);
  }

  private loadCustomerCard(customerId?: number, switchToCompany = false) {
    this.customerCard = undefined;
    if (!customerId) return;
    this.customerCardLoading = true;
    this.customerService.find(customerId).toPromise()
      .then((res) => {
        // Bu arada başka müşteri seçildiyse eski yanıtı yok say.
        if (this.request.customerId !== customerId) return;
        this.customerCardLoading = false;
        const customer = res?.data as Customer;
        if (!res?.isSuccess || !customer) return;
        this.customerCard = customer;
        if (switchToCompany && customer.customerType === CustomerType.Company) {
          this.request.customerType = CustomerType.Company;
        }
        this.prefillTcknFromCard(customer);
      })
      .catch(() => {
        if (this.request.customerId === customerId) this.customerCardLoading = false;
      });
  }

  /**
   * Gerçek kişi kartındaki TCKN forma önerilir — alan boşsa ya da önceki müşterinin kartından
   * gelmişse. Personelin elle yazdığı TCKN değiştirilmez. Tüzel kişide yetkilinin TCKN'si
   * şirket kartında yok.
   */
  private prefillTcknFromCard(card: Customer) {
    if (this.isCompany || card.customerType === CustomerType.Company) return;
    const current = this.request.tckn?.trim();
    if (current && current !== this.prefilledTckn) return;
    const tckn = card.tckn?.trim() || undefined;
    this.request.tckn = tckn;
    this.prefilledTckn = tckn;
  }

  onAttachmentsSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    const allowed = this.attachmentAccept.split(',');
    const rejected: string[] = [];
    for (const file of files) {
      if (file.size > this.attachmentMaxBytes || !allowed.includes(file.type)) {
        rejected.push(file.name);
      } else {
        this.pendingAttachments.push(file);
      }
    }
    if (rejected.length) {
      this.alertService.showErrorsToast([`Yalnızca en fazla 10 MB PDF/JPG/PNG yüklenebilir: ${rejected.join(', ')}`]);
    }
    // Aynı dosya tekrar seçilebilsin.
    input.value = '';
  }

  removePendingAttachment(index: number) {
    this.pendingAttachments.splice(index, 1);
  }

  deleteExistingAttachment(attachment: CustomerIdentityVerificationAttachment) {
    if (!this.editId || !attachment.id) return;
    this.alertService.showDeleteAlert().then((result) => {
      if (!result.isConfirmed) return;
      this.deletingAttachmentId = attachment.id;
      this.service.deleteAttachment(this.editId!, attachment.id!).toPromise()
        .then((res) => {
          this.deletingAttachmentId = undefined;
          if (res.isSuccess) this.existingAttachments = res.data?.attachments ?? [];
        })
        .catch(() => {
          this.deletingAttachmentId = undefined;
          this.alertService.showErrorsToast(['Belge silinemedi.']);
        });
    });
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
  }

  /**
   * Backend ValidateLegalEntity (+ yeni müşteride ValidateCompanyCard) ile aynı liste —
   * eksikler tek mesajda gösterilir. Kayıtlı müşteride şirket bilgileri karttan gelir.
   */
  private missingLegalEntityFields(): string[] {
    const r = this.request;
    const missing: string[] = [];
    if (!this.usesCustomerCard) {
      if (!r.companyTitle?.trim()) missing.push('Şirket Unvanı');
      if (!r.taxNumber?.trim()) missing.push('Vergi Kimlik Numarası');
      if (!r.companyPhone?.trim()) missing.push('Telefon Numarası');
      if (!r.companyAddress?.trim()) missing.push('Şirket Açık Adresi');
    }
    if (!r.tradeRegistryNumber?.trim()) missing.push('Ticaret Sicil Numarası');
    if (!r.businessActivity?.trim()) missing.push('Faaliyet Konusu');
    if (!r.representativeFirstName?.trim()) missing.push('Yetkili Adı');
    if (!r.representativeLastName?.trim()) missing.push('Yetkili Soyadı');
    if (!r.birthDate) missing.push('Yetkili Doğum Tarihi');
    if (!r.nationality?.trim()) missing.push('Yetkili Uyruğu');
    if (!r.representationAuthority?.trim()) missing.push('Temsil Yetkileri');
    return missing;
  }

  setCustomerMode(isNew: boolean) {
    this.isNewCustomer = isNew;
    this.customerCard = undefined;
    this.request.customerId = undefined;
    this.request.newCustomer = undefined;
    this.newCustomer = {};
    if (this.prefilledTckn && this.request.tckn === this.prefilledTckn) this.request.tckn = undefined;
    this.prefilledTckn = undefined;
  }

  onDocumentTypeChange() {
    // Tckn dışında arka yüz kavramı yok — seçiliyse temizle. Kimlikten okuma yalnızca T.C. Kimlik Kartı'nda.
    if (!this.isTckn) this.documentBackFile = undefined;
    // Önceki belge türünün okuması yeni türe uygulanmasın; okunabilirse yeni türle yeniden oku.
    this.clearOcr();
    if (this.canReadOcr) this.readIdentity();
    this.refreshIdentityUploadItems(); // ön yüz etiketi belge türüne bağlı
  }

  onFrontFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.documentFrontFile = input.files?.[0] ?? undefined;
    this.ocrFrontOverride = undefined;
    if (this.documentFrontFile) this.ensurePersonForIdentityFile();
    this.onIdentityImagesChanged();
  }

  onBackFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.documentBackFile = input.files?.[0] ?? undefined;
    this.ocrBackOverride = undefined;
    if (this.documentBackFile) this.ensurePersonForIdentityFile();
    this.onIdentityImagesChanged();
  }

  /**
   * Ön ve arka yüzün fotoğrafı İKİSİ DE seçilince okuma kendiliğinden başlar ve sonuç forma yazılır;
   * biri değiştirilirse iki yüzle yeniden okunur (önceki okuma kesilir). Bir yüz eksikse ya da
   * okunamayan (PDF) bir dosyaysa sonuç temizlenir — eski görselin okuması (ve ad denetimi) yeni
   * dosyaya uygulanmasın.
   */
  private onIdentityImagesChanged() {
    if (!this.ocrEnabled) return;
    if (this.canReadOcr) {
      this.readIdentity();
    } else {
      this.clearOcr();
    }
  }

  /** Kayıt oluşturulduktan/güncellendikten SONRA seçilen belge(ler) yüklenir — create akışında id gerekir. */
  get hasFilesToUpload(): boolean {
    return !!(this.documentFrontFile || this.documentBackFile || this.pendingAttachments.length);
  }

  /**
   * Gömülü kullanımda beyan formuna eklenecek kimlik tespiti isteği. Karttaki farklı TCKN burada
   * değiştirilmez (onay penceresi yalnızca bu modalın kendi kaydında) — backend kartı olduğu gibi bırakır.
   */
  getRequest(): CustomerIdentityVerificationRequest {
    return { ...this.payload(), newCustomer: undefined, overwriteCustomerTckn: false };
  }

  /**
   * Sunucuya gidecek istek: TCKN yalnızca T.C. Kimlik Kartı'nda gönderilir (diğer belgelerde alan
   * gizli); seri nosu bilinmeyen eski kayıtta seri no girilmediyse eski belge no (TCKN) korunur.
   */
  private payload(): CustomerIdentityVerificationRequest {
    const r = this.request;
    return {
      ...r,
      documentNumber: r.documentNumber?.trim() || (this.isTckn ? this.legacyDocumentNumber : undefined) || r.documentNumber,
      tckn: this.showTcknField ? r.tckn?.trim() || undefined : undefined,
      documentExpiryDate: (r.documentExpiryDate as any) || undefined,
    };
  }

  /** Belge numarası, TCKN ve son geçerlilik denetimi — backend MasakComplianceRules.IdentityDocumentError ile aynı. */
  private identityDocumentError(): string | null {
    const payload = this.payload();
    if (!payload.documentNumber?.trim()) {
      if (this.isTckn) {
        return this.isCompany ? 'Yetkilinin kimlik kartı seri numarasını giriniz!' : 'Kimlik kartının seri numarasını giriniz!';
      }
      return this.isCompany ? 'Yetkilinin kimlik belgesi numarasını giriniz!' : 'Belge numarasını giriniz!';
    }
    if (this.isTckn && !payload.tckn) {
      return this.isCompany ? 'Yetkilinin T.C. kimlik numarasını giriniz!' : 'T.C. kimlik numarasını giriniz!';
    }
    if (payload.tckn && !isValidTckn(payload.tckn)) {
      return this.isCompany ? 'Yetkilinin T.C. kimlik numarası geçersiz!' : 'T.C. kimlik numarası geçersiz!';
    }
    const expiry = payload.documentExpiryDate ? String(payload.documentExpiryDate).substring(0, 10) : '';
    const birth = payload.birthDate ? String(payload.birthDate).substring(0, 10) : '';
    if (expiry && birth && expiry <= birth) {
      return 'Belgenin son geçerlilik tarihi doğum tarihinden sonra olmalıdır!';
    }
    return null;
  }

  /** Süresi dolmuş belge kaydı engellemez (masak-kimlik-ocr.md K3) — personel bir kez daha onaylar. */
  private async confirmExpiredDocument(): Promise<boolean> {
    if (!this.documentExpired) return true;
    const expiry = String(this.request.documentExpiryDate).substring(0, 10).split('-').reverse().join('.');
    const result = await this.alertService.showConfirmAlert(
      `Kimlik belgesinin süresi ${expiry} tarihinde dolmuş. Yine de kaydedilsin mi?`, 'Kaydet', 'Geri Dön');
    return !!result.isConfirmed;
  }

  /**
   * Kayıtlı müşterinin kartında farklı TCKN varsa kart sessizce değişmez: true = kart güncellensin,
   * false = kart olduğu gibi kalsın (kayıt yine açılır), null = pencere kapatıldı, kaydetme.
   */
  private async confirmCustomerTcknOverwrite(): Promise<boolean | null> {
    if (!this.tcknDiffersFromCard) return false;
    const result = await this.alertService.showConfirmAlert(
      `Müşteri kartındaki TCKN (${this.customerCard!.tckn}) kimlikteki TCKN'den (${this.request.tckn!.trim()}) farklı. `
      + 'Müşteri kartı kimlikteki TCKN ile güncellensin mi?',
      'Kartı Güncelle', 'Kartı Değiştirme');
    if (result.isConfirmed) return true;
    return result.dismiss === Swal.DismissReason.cancel ? false : null;
  }

  async uploadSelectedDocuments(id: number): Promise<void> {
    if (this.documentFrontFile) {
      await this.service.uploadDocument(id, this.documentFrontFile, 'front').toPromise();
    }
    if (this.isTckn && this.documentBackFile) {
      await this.service.uploadDocument(id, this.documentBackFile, 'back').toPromise();
    }
    for (const file of this.pendingAttachments) {
      await this.service.uploadAttachment(id, file).toPromise();
    }
  }

  /**
   * Kaydetmeden önceki denetim — ilk hatanın mesajını döner, sorun yoksa null. Gömülü
   * kullanımda müşteri, işlem tutarı ve mahiyet beyanı beyan formundan geldiği için atlanır.
   */
  validate(): string | null {
    if (!this.isEdit && !this.embedded) {
      if (this.isNewCustomer && this.isCompany) {
        // Tüzel kişide yeni müşteri kartı şirket bilgilerinden açılır (backend).
        this.request.newCustomer = undefined;
        this.request.customerId = undefined;
      } else if (this.isNewCustomer) {
        if (!this.newCustomer.firstName?.trim() || !this.newCustomer.lastName?.trim()) {
          return 'Yeni müşteri için ad ve soyad giriniz!';
        }
        this.request.newCustomer = this.newCustomer;
        this.request.customerId = undefined;
      } else if (!this.request.customerId) {
        return 'Müşteri seçiniz!';
      }
    }
    if (this.isCompany && this.usesCustomerCard && this.customerCardMissing.length) {
      return `Seçilen müşteri kartında eksik bilgi var: ${this.customerCardMissing.join(', ')}. Önce müşteri kartını güncelleyiniz.`;
    }
    if (this.isCompany) {
      const missing = this.missingLegalEntityFields();
      if (missing.length) {
        return `Tüzel kişi için zorunlu alanlar eksik: ${missing.join(', ')}`;
      }
    }
    if (this.ocrReading) return 'Kimlik okunuyor — okuma bitince kaydediniz ya da okumayı iptal ediniz.';
    if (this.ocrCustomerSearching) return 'Kimlikteki TCKN ile kayıtlı müşteri aranıyor — birkaç saniye bekleyiniz.';
    if (this.ocrResult?.firstName && !this.isCompany && this.usesCustomerCard && this.customerCardLoading) {
      return 'Müşteri kartı yükleniyor — kimlikteki adla karşılaştırılabilmesi için bekleyiniz.';
    }
    if (this.ocrNameMismatchBlocks) {
      return `Kimlikteki ad (${this.ocrResult!.firstName} ${this.ocrResult!.lastName}) seçili müşteriyle (${this.customerCard!.name}) uyuşmuyor. `
        + 'Doğru müşteriyi seçin ya da müşteri kartındaki adı güncelleyin.';
    }
    const identityError = this.identityDocumentError();
    if (identityError) return identityError;
    if (this.embedded) return null;
    if (!this.request.transactionAmountTl) {
      return 'İşlem tutarını giriniz!';
    }
    if (this.purposeRequired
      && (this.request.transactionPurposeDescription ?? '').trim().length < this.purposeMinLength) {
      return `${this.purposeBandMinTl.toLocaleString('tr-TR')}-${this.purposeBandMaxTl.toLocaleString('tr-TR')} TL bandında en az ${this.purposeMinLength} karakterlik işlem mahiyeti beyanı zorunludur!`;
    }
    return null;
  }

  /** Yeni imza varsa gönderilir; düzenlemede kayıtlı imza kaldırıldıysa silinir. */
  private async persistSignature(id: number): Promise<void> {
    if (this.signature && !isSignatureEmpty(this.signature)) {
      const res = await this.service.saveSignature('identity', id, this.signature).toPromise();
      if (!res.isSuccess) throw new Error('İmza kaydedilemedi.');
    } else if (this.isEdit && this.existingSignatureSvg && this.existingSignatureRemoved) {
      await this.service.deleteSignature('identity', id).toPromise();
    }
  }

  async save() {
    const error = this.validate() ?? signatureError(this.signature);
    if (error) {
      this.alertService.showErrorToast(error);
      return;
    }

    if (!(await this.confirmExpiredDocument())) return;
    const overwriteCustomerTckn = await this.confirmCustomerTcknOverwrite();
    if (overwriteCustomerTckn === null) return;

    const payload: CustomerIdentityVerificationRequest = { ...this.payload(), overwriteCustomerTckn };
    this.saving = true;
    const action = this.isEdit
      ? this.service.updateVerification(this.editId!, payload as CustomerIdentityVerificationUpdateRequest)
      : this.service.createVerification(payload);

    action.toPromise()
      .then(async (res) => {
        if (!res.isSuccess) {
          this.saving = false;
          return;
        }
        const id = this.isEdit ? this.editId! : res.data?.id;
        if (id && this.hasFilesToUpload) {
          try {
            await this.uploadSelectedDocuments(id);
          } catch {
            this.saving = false;
            this.alertService.showErrorsToast(['Kayıt kaydedildi ama belge yüklenemedi.']);
            this.activeModal.close(true);
            return;
          }
        }
        if (id) {
          try {
            await this.persistSignature(id);
          } catch {
            // Kayıt kaydedildi, yalnızca imza gitmedi — düzenlemeden yeniden imzalanabilir.
            this.saving = false;
            this.alertService.showErrorsToast(['Kayıt kaydedildi ama imza kaydedilemedi.']);
            this.activeModal.close(true);
            return;
          }
        }
        this.saving = false;
        // Kayıt açılınca sunucu müşteriyi yaptırım listelerine karşı otomatik tarar; olası
        // eşleşme işlemi engellemez ama personel hemen haberdar olsun. Sunucunun kaydı
        // engellemeyen uyarıları (karttaki farklı TCKN, mükerrer TCKN) da aynı pencerede.
        const notices: string[] = [];
        if (res.data?.latestScreening?.result === SanctionScreeningResult.PossibleMatch
          && res.data.latestScreening.reviewStatus === SanctionScreeningReviewStatus.Pending) {
          notices.push('Yaptırım listesi taramasında OLASI EŞLEŞME bulundu — MASAK Uyum > Yaptırım Taramaları\'ndan inceleyiniz.');
        }
        notices.push(...(res.data?.warnings ?? []));
        if (notices.length) {
          this.alertService.showWarning(`Kayıt kaydedildi. ${notices.join(' ')}`);
        }
        // Başarılı kayıtta ayrıca "kaydedildi" uyarısı GÖSTERİLMEZ: tarama sonucunun SignalR
        // bildirimi (MasakHubService, SweetAlert toast) aynı anda geliyor ve SweetAlert tek pencere
        // gösterdiği için bu uyarı bildirimin yerine geçiyordu.
        this.activeModal.close(true);
      })
      .catch(() => {
        this.saving = false;
        this.alertService.showErrorsToast([this.isEdit ? 'Kayıt güncellenemedi.' : 'Kayıt oluşturulamadı.']);
      });
  }
}
